import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  format,
  isSameMonth,
  isToday,
  parseISO,
  isWithinInterval,
  startOfDay,
  endOfDay,
} from 'date-fns';
import { Meeting } from '../../../shared/types';
import { MeetingEvent } from './MeetingEvent';
import { useTimezone } from '../../../context/TimezoneContext';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { convertToZonedTime } from '../../../utils/datetime';

interface MonthViewProps {
  currentDate: Date;
  meetings: Meeting[];
  onMeetingClick: (meeting: Meeting) => void;
}

export const MonthView: React.FC<MonthViewProps> = ({
  currentDate,
  meetings,
  onMeetingClick,
}) => {
  const { t } = useTranslation();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { timezone } = useTimezone();
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
  
  // Calculate the start day offset (0 = Monday, 6 = Sunday)
  const startDayOffset = monthStart.getDay();
  const daysToAdd = startDayOffset === 0 ? 6 : startDayOffset - 1;
  
  // Add empty cells for the days before the first of the month
  const emptyCells = Array(daysToAdd).fill(null);
  
  // Calculate suffix days to complete the 42-cell grid
  const totalDays = emptyCells.length + days.length;
  const suffixDays = Array(42 - totalDays).fill(null);

  const getMeetingsForDay = (day: Date) => {
    return meetings.filter(meeting => {
      const meetingDate = parseISO(meeting.fields['Fecha Reunión']);
      // const zonedDate = convertToZonedTime(meetingDate, timezone); // May be needed for timezone display
      return isWithinInterval(meetingDate, {
        start: startOfDay(day),
        end: endOfDay(day),
      });
    })
    .sort((a, b) => {
      const dateA = parseISO(a.fields['Fecha Reunión']).getTime();
      const dateB = parseISO(b.fields['Fecha Reunión']).getTime();
      return dateA - dateB;
    });
  };

  return (
    <div className="bg-white shadow-sm border border-gray-200">
      <div className="grid grid-cols-7 border-b border-gray-200">
        {['L', 'M', 'X', 'J', 'V', 'S', 'D'].map(day => (
          <div
            key={day}
            className="px-2 py-3 text-xs font-medium text-gray-500 text-center"
          >
            {day}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {emptyCells.map((_, index) => (
          <div
            key={`empty-${index}`}
            className="border-b border-r border-gray-200 bg-gray-50 h-32"
          />
        ))}
        {days.map((day) => {
          const dayMeetings = getMeetingsForDay(day);
          const isCurrentMonth = isSameMonth(day, currentDate);
          const showPlusNIndicator = dayMeetings.length > 3;
          const remainingMeetingsCount = showPlusNIndicator ? dayMeetings.length - 3 : 0;

          return (
            <div
              key={day.toISOString()}
              className={`relative border-b border-r border-gray-200 p-2 flex flex-col h-32 ${
                !isCurrentMonth ? 'bg-gray-50' : ''
              }`}
            >
              <div className="flex justify-between items-start mb-1">
                <span
                  className={`inline-flex items-center justify-center w-6 h-6 text-sm ${
                    isToday(day)
                      ? 'bg-[#0055FF] text-white font-medium'
                      : isCurrentMonth
                      ? 'text-gray-900'
                      : 'text-gray-400'
                  }`}
                >
                  {format(day, 'd')}
                </span>
                {showPlusNIndicator && remainingMeetingsCount > 0 && (
                  <span className="text-xs text-gray-500">
                    +{remainingMeetingsCount}
                  </span>
                )}
              </div>
              <div className="space-y-1 overflow-y-auto flex-1 min-h-0">
                {dayMeetings.map(meeting => (
                  <MeetingEvent
                    key={meeting.id}
                    meeting={meeting}
                    onClick={() => onMeetingClick(meeting)}
                  />
                ))}
              </div>
            </div>
          );
        })}
        {suffixDays.map((_, index) => (
          <div
            key={`suffix-${index}`}
            className="border-b border-r border-gray-200 bg-gray-50 h-32"
          />
        ))}
      </div>
    </div>
  );
};